import React, { useMemo } from 'react';
import {
    Box,
    Typography,
    Paper,
    Grid,
    Card,
    CardContent,
    CardHeader,
    Chip,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    useTheme
} from '@mui/material';
import {
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    Security as SecurityIcon,
    Category as CategoryIcon,
    Schedule as ScheduleIcon,
    Settings as SettingsIcon,
    ExpandMore as ExpandMoreIcon,
    Warning as WarningIcon
} from '@mui/icons-material';
import { SandboxConsentDetails } from '../../../services/sandbox-consents-service';

interface PurposesTabProps {
    consentData: SandboxConsentDetails;
}

const PurposesTab: React.FC<PurposesTabProps> = ({ consentData }) => {
    const theme = useTheme();
    const purposes = consentData.purposes || [];

    // Calculate summary statistics
    const summaryStats = useMemo(() => {
        const total = purposes.length;
        const consented = purposes.filter(p => p.consented).length;
        const declined = total - consented;
        return { total, consented, declined };
    }, [purposes]);

    const getConsentStatusColor = (consented: boolean) => {
        return consented ? 'success' : 'error';
    };

    const getConsentStatusIcon = (consented: boolean) => {
        return consented ? <CheckCircleIcon /> : <CancelIcon />;
    };

    const formatRetentionPeriod = (retention: any) => {
        if (retention.type.code === 'TIME_BASED' && retention.timeBasedPeriod && retention.timeBasedUnit) {
            return `${retention.timeBasedPeriod} ${retention.timeBasedUnit.label}`;
        }
        return 'Event-based';
    };

    const formatPreferenceValue = (preference: any) => {
        if (preference.selectedOptions.length > 1) {
            return preference.selectedOptions.map((opt: any) => opt.label).join(', ');
        }
        return preference.preferenceValue;
    };

    return (
        <Box sx={{ p: 3 }}>
            {/* Summary Cards */}
            <Box sx={{ mb: 3 }}>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                        <Paper sx={{ p: 2, textAlign: 'center' }}>
                            <Typography variant="h4" color="primary">
                                {summaryStats.total}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Total Purposes
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                        <Paper sx={{ p: 2, textAlign: 'center' }}>
                            <Typography variant="h5" color="success.main">
                                {summaryStats.consented}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Consented
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                        <Paper sx={{ p: 2, textAlign: 'center' }}>
                            <Typography variant="h5" color="error.main">
                                {summaryStats.declined}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Declined
                            </Typography>
                        </Paper>
                    </Grid>
                </Grid>
            </Box>

            {/* Purpose Cards */}
            <Typography variant="h6" gutterBottom>
                Purpose Details ({purposes.length})
            </Typography>

            {purposes.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="body1" color="text.secondary">
                        No purposes found for this consent.
                    </Typography>
                </Paper>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {purposes.map((purpose) => (
                                <Card key={purpose.id} sx={{ width: '100%' }}>
                                    <CardHeader
                                        title={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                <Typography variant="h6">
                                                    {purpose.name}
                                                </Typography>
                                                <Chip
                                                    icon={getConsentStatusIcon(purpose.consented)}
                                                    label={purpose.consented ? 'Consented' : 'Declined'}
                                                    color={getConsentStatusColor(purpose.consented)}
                                                    size="small"
                                                />
                                                <Chip
                                                    icon={<CategoryIcon />}
                                                    label={purpose.purposeCategory.label}
                                                    variant="outlined"
                                                    size="small"
                                                />
                                                {purpose.isRequired && (
                                                    <Chip
                                                        icon={<WarningIcon />}
                                                        label="Required"
                                                        color="error"
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                )}
                                            </Box>
                                        }
                                    />
                                    <CardContent>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                            {purpose.description}
                                        </Typography>

                                        {/* Lawful Ground - Inline Display */}
                                        <Box sx={{ mb: 2 }}>
                                            <Typography variant="body2" sx={{ mb: 1 }}>
                                                <strong>Lawful Ground:</strong> {purpose.lawfulGround.label} ({purpose.lawfulGround.code})
                                            </Typography>
                                            {purpose.lawfulGround.description && (
                                                <Typography variant="body2" color="text.secondary">
                                                    {purpose.lawfulGround.description}
                                                </Typography>
                                            )}
                                        </Box>

                                        {/* Retention Policy Section */}
                                        <Box sx={{ mb: 2 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                <ScheduleIcon color="primary" fontSize="small" />
                                                <Typography variant="subtitle2">Retention Policy</Typography>
                                            </Box>
                                            <Grid container spacing={1} sx={{ ml: 1 }}>
                                                <Grid item xs={12}>
                                                    <Typography variant="body2">
                                                        <strong>Type:</strong> {purpose.retention.type.label}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <Typography variant="body2">
                                                        <strong>Start:</strong> {purpose.retention.startDateType.label}
                                                        {purpose.retention.timeBasedPeriod && (
                                                            <> • <strong>Duration:</strong> {formatRetentionPeriod(purpose.retention)}</>
                                                        )}
                                                        {purpose.retention.expirationDate && (
                                                            <> • <strong>Expires:</strong> {new Date(purpose.retention.expirationDate).toLocaleDateString()}</>
                                                        )}
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                            <Box sx={{ mt: 1, ml: 1 }}>
                                                <Typography variant="body2" sx={{ mb: 0.5 }}>
                                                    <strong>End Conditions:</strong>
                                                </Typography>
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                    {Object.entries(purpose.retention.endConditions).map(([key, value]) => (
                                                        <Chip
                                                            key={key}
                                                            icon={value ? <CheckCircleIcon /> : <CancelIcon />}
                                                            label={key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                                                            color={value ? 'success' : 'error'}
                                                            size="small"
                                                            variant="outlined"
                                                        />
                                                    ))}
                                                </Box>
                                            </Box>
                                        </Box>

                                        {/* Preferences Section */}
                                        {purpose.preferences && purpose.preferences.length > 0 && (
                                            <Accordion>
                                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <SettingsIcon color="primary" />
                                                        <Typography variant="subtitle2">
                                                            Preferences ({purpose.preferences.length})
                                                        </Typography>
                                                    </Box>
                                                </AccordionSummary>
                                                <AccordionDetails>
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                        {purpose.preferences.map((preference) => (
                                                            <Paper key={preference.id} sx={{ p: 2 }}>
                                                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                                                    {preference.name}
                                                                </Typography>
                                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                                    {preference.description}
                                                                </Typography>
                                                                <Typography variant="body2">
                                                                    <strong>Selected:</strong> {formatPreferenceValue(preference)}
                                                                </Typography>
                                                                {preference.selectedOptions.length > 0 && (
                                                                    <Box sx={{ mt: 1 }}>
                                                                        <Typography variant="body2" sx={{ mb: 1 }}>
                                                                            <strong>Selected Options:</strong>
                                                                        </Typography>
                                                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                                            {preference.selectedOptions.map((option) => (
                                                                                <Chip
                                                                                    key={option.id}
                                                                                    label={option.label}
                                                                                    size="small"
                                                                                    color="primary"
                                                                                    variant="outlined"
                                                                                />
                                                                            ))}
                                                                        </Box>
                                                                    </Box>
                                                                )}
                                                            </Paper>
                                                        ))}
                                                    </Box>
                                                </AccordionDetails>
                                            </Accordion>
                                        )}
                                    </CardContent>
                                </Card>
                            ))}
                </Box>
            )}
        </Box>
    );
};

export default PurposesTab;
